import { Injectable, type ErrorHandler as AngularErrorHandler } from '@angular/core';
import { captureMessage, reportUnhandledError } from '../services/sentry-client';
import { isChunkLoadError, recoverFromChunkError } from './chunk-load-error';

/**
 * Angular `ErrorHandler` that keeps the Sentry SDK off the critical path.
 *
 * Stands in for `Sentry.createErrorHandler()`, which cannot be used directly
 * here: building it at bootstrap would force a static import of the SDK. This
 * handler forwards to the real Sentry handler once the deferred chunk lands,
 * and buffers anything raised before that — see `sentry-client.ts`.
 *
 * It is also the GLOBAL backstop for stale lazy-chunk failures: the router's
 * `withNavigationErrorHandler` recovers route-load failures, but a failed
 * dynamic import from a `@defer` block (or any non-navigation import) surfaces
 * here as an unhandled rejection instead. Both paths share one detector.
 */
@Injectable()
export class SentryErrorHandler implements AngularErrorHandler {
  handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      // Best-effort breadcrumb (may not flush before the reload) so the rate is
      // observable in Sentry without paging on a self-healing condition.
      captureMessage('Recovered from stale lazy-chunk (global handler)', { level: 'warning' });
      // No navigation target here — reload the current URL to pull the new build.
      if (recoverFromChunkError(currentUrl())) return;
      // Recovery suppressed (offline, or already retried): fall through so the
      // genuine failure is still reported rather than swallowed.
    }
    reportUnhandledError(error);
  }
}

function currentUrl(): string {
  if (typeof window === 'undefined') return '/';
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}` || '/';
}
